import type { ReactNode } from "react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, GradientText, RevealItem, RevealList, SlideShell } from "@/ui";

const TERMS: { term: string; expansion: string; definition: ReactNode; footnote?: string }[] = [
  {
    term: "SERP",
    expansion: "Search engine results page",
    definition: (
      <>
        What users see after a query —{" "}
        <span className="font-semibold text-white">organic</span>,{" "}
        <span className="font-semibold text-white">ads</span>, and{" "}
        <span className="font-semibold text-white">features</span>.
      </>
    ),
  },
  {
    term: "E-E-A-T",
    expansion: "Experience · Expertise · Authority · Trust",
    definition: (
      <>
        How Google&rsquo;s guidelines talk about{" "}
        <span className="font-semibold text-white">page quality</span>.
      </>
    ),
  },
  {
    term: "GSC",
    expansion: "Google Search Console",
    definition: (
      <>
        <span className="font-semibold text-white">Impressions</span>,{" "}
        <span className="font-semibold text-white">clicks</span>, and{" "}
        <span className="font-semibold text-white">indexing status</span> for your verified
        properties.
      </>
    ),
  },
  {
    term: "CWV",
    expansion: "Core Web Vitals",
    definition: (
      <>
        <span className="font-mono font-semibold text-white">LCP · INP · CLS</span> — part of the{" "}
        <span className="font-semibold text-white">page experience</span> story.
      </>
    ),
    footnote: "INP replaced FID in 2024.",
  },
];

export function GlossarySlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={7} title="Quick glossary">
      <RevealList
        active={active}
        stagger={0.25}
        delay={0.2}
        className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-8"
      >
        {TERMS.map((t) => (
          <RevealItem key={t.term} className="h-full">
            <GlassCard className="flex h-full flex-col gap-4 px-9 py-8 transition-transform duration-300 hover:-translate-y-1">
              <span className="font-mono text-5xl font-semibold tracking-tight">
                <GradientText>{t.term}</GradientText>
              </span>
              <p className="text-xl font-medium uppercase tracking-widest text-white/50">
                {t.expansion}
              </p>
              <p className="text-2xl leading-relaxed text-white/85">{t.definition}</p>
              {t.footnote && (
                <p className="mt-auto text-xl italic text-white/50">{t.footnote}</p>
              )}
            </GlassCard>
          </RevealItem>
        ))}
      </RevealList>
    </SlideShell>
  );
}
